import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, MessageSquare, Calendar, User, X } from 'lucide-react';
import { api } from '../auth/authStore';
import toast from 'react-hot-toast';

interface ClientNote {
  id: string;
  title: string;
  noteText: string;
  clientId: string;
  createdBy: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  createdAt: string;
  updatedAt: string;
}

interface ClientNotesProps {
  clientId: string;
  clientName?: string;
  showHeader?: boolean;
  showAddButton?: boolean;
  maxHeight?: string;
}

interface NoteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  note?: ClientNote | null;
  clientId: string;
  mode: 'create' | 'edit';
}

const NoteModal: React.FC<NoteModalProps> = ({ isOpen, onClose, onSuccess, note, clientId, mode }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    noteText: '',
  });

  useEffect(() => {
    if (isOpen) {
      if (mode === 'edit' && note) {
        setFormData({
          title: note.title,
          noteText: note.noteText,
        });
      } else {
        setFormData({
          title: '',
          noteText: '',
        });
      }
    }
  }, [isOpen, mode, note]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      toast.error('Note title is required');
      return;
    }
    
    if (!formData.noteText.trim()) {
      toast.error('Note text is required');
      return;
    }

    setLoading(true);
    
    try {
      const payload = {
        title: formData.title.trim(),
        noteText: formData.noteText.trim(),
        ...(mode === 'create' && { clientId }),
      };

      const response = mode === 'create'
        ? await api.post('/client-notes', payload)
        : await api.patch(`/client-notes/${note?.id}`, payload);

      if (response.data.success) {
        toast.success(`Note ${mode === 'create' ? 'created' : 'updated'} successfully`);
        onSuccess();
        onClose();
      }
    } catch (error: any) {
      console.error(`Error ${mode === 'create' ? 'creating' : 'updating'} note:`, error);
      toast.error(error.response?.data?.message || `Failed to ${mode} note`);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">
            {mode === 'create' ? 'Add Client Note' : 'Edit Client Note'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            disabled={loading}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Note Title *
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Enter note title"
              required
              disabled={loading}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Note Text *
            </label>
            <textarea
              value={formData.noteText}
              onChange={(e) => setFormData(prev => ({ ...prev, noteText: e.target.value }))}
              rows={8}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Enter note details..."
              required
              disabled={loading}
            />
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading}
            >
              {loading ? 'Saving...' : (mode === 'create' ? 'Add Note' : 'Update Note')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export const ClientNotes: React.FC<ClientNotesProps> = ({
  clientId,
  clientName,
  showHeader = true,
  showAddButton = true,
  maxHeight = "400px"
}) => {
  const [notes, setNotes] = useState<ClientNote[]>([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingNote, setEditingNote] = useState<ClientNote | null>(null);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');

  useEffect(() => {
    if (clientId) {
      fetchNotes();
    }
  }, [clientId]);

  const fetchNotes = async () => {
    if (!clientId) return;
    
    setLoading(true);
    try {
      const response = await api.get(`/client-notes/client/${clientId}`);
      if (response.data.success) {
        setNotes(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching client notes:', error);
      toast.error('Failed to load client notes');
    } finally {
      setLoading(false);
    }
  };

  const handleAddNote = () => {
    setEditingNote(null);
    setModalMode('create');
    setShowModal(true);
  };

  const handleEditNote = (note: ClientNote) => {
    setEditingNote(note);
    setModalMode('edit');
    setShowModal(true);
  };

  const handleDeleteNote = async (noteId: string) => {
    if (!confirm('Are you sure you want to delete this note?')) {
      return;
    }

    try {
      const response = await api.delete(`/client-notes/${noteId}`);
      if (response.data.success) {
        toast.success('Note deleted successfully');
        fetchNotes();
      }
    } catch (error: any) {
      console.error('Error deleting note:', error);
      toast.error(error.response?.data?.message || 'Failed to delete note');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200">
      {showHeader && (
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-primary-600" />
            <h3 className="text-lg font-semibold text-gray-900">
              Client Notes {clientName && `- ${clientName}`}
            </h3>
          </div>
          <button
            onClick={handleAddNote}
            className="flex items-center gap-2 px-3 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            <Plus className="w-4 h-4" />
            Add Note
          </button>
        </div>
      )}

      {!showHeader && showAddButton && (
        <div className="flex justify-end p-4 border-b border-gray-200">
          <button
            onClick={handleAddNote}
            className="flex items-center gap-2 px-3 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            <Plus className="w-4 h-4" />
            Add Note
          </button>
        </div>
      )}

      <div className="p-4" style={{ maxHeight, overflowY: 'auto' }}>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : notes.length === 0 ? (
          <div className="text-center py-8">
            <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No notes found for this client</p>
            {!showHeader && (
              <button
                onClick={handleAddNote}
                className="mt-4 flex items-center gap-2 px-3 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 mx-auto"
              >
                <Plus className="w-4 h-4" />
                Add First Note
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {notes.map((note) => (
              <div key={note.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow">
                <div className="flex items-start justify-between mb-2">
                  <h4 className="font-semibold text-gray-900">{note.title}</h4>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleEditNote(note)}
                      className="p-1 text-gray-400 hover:text-primary-600"
                      title="Edit note"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteNote(note.id)}
                      className="p-1 text-gray-400 hover:text-red-600"
                      title="Delete note"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                
                <p className="text-gray-700 text-sm mb-3 whitespace-pre-wrap">{note.noteText}</p>
                
                <div className="flex items-center gap-4 text-xs text-gray-500">
                  <div className="flex items-center gap-1">
                    <User className="w-3 h-3" />
                    <span>{note.createdBy.firstName} {note.createdBy.lastName}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    <span>{formatDate(note.createdAt)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <NoteModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onSuccess={fetchNotes}
        note={editingNote}
        clientId={clientId}
        mode={modalMode}
      />
    </div>
  );
};
